import React, { useState, useEffect, useCallback } from 'react';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { Plus, Trash2, Edit2, Save, X, Loader2, Eye, EyeOff, RefreshCw, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';
import { FAQ_ITEMS as FAQ_SEED } from '@/data/faqContent';

const CATEGORIES = [
  { id: 'getting-started',   label: 'Getting started' },
  { id: 'chat',              label: 'Chatting with Eili' },
  { id: 'uploads',           label: 'Files & analysis' },
  { id: 'bible',             label: 'Bible reader' },
  { id: 'community',         label: 'Community' },
  { id: 'sharing',           label: 'Sharing & saving' },
  { id: 'subscription',      label: 'Subscription & billing' },
  { id: 'account',           label: 'Account & settings' },
  { id: 'troubleshooting',   label: 'Troubleshooting' },
];

/* Slugify an arbitrary question into a stable kebab-case id. */
const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60);

function EditorForm({ item, onSave, onCancel, colors, saving, isNew }) {
  const [form, setForm] = useState(() => ({
    id: item?.id || '',
    category: item?.category || CATEGORIES[0].id,
    question: item?.question || '',
    answer: item?.answer || '',
    tags: (item?.tags || []).join(', '),
    screenshots: item?.screenshots || [],
    order: item?.order ?? 0,
    published: item?.published !== false,
  }));

  const [newShot, setNewShot] = useState({ src: '', alt: '', viewport: 'mobile' });

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const q = form.question.trim();
    if (!q) { toast.error('Question is required'); return; }
    if (!form.answer.trim()) { toast.error('Answer is required'); return; }
    const payload = {
      id: (form.id || slugify(q)).trim(),
      category: form.category,
      question: q,
      answer: form.answer.trim(),
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      screenshots: form.screenshots,
      order: Number(form.order) || 0,
      published: !!form.published,
    };
    onSave(payload);
  };

  const addShot = () => {
    if (!newShot.src.trim()) { toast.error('Screenshot src is required'); return; }
    set('screenshots', [...form.screenshots, { ...newShot, src: newShot.src.trim() }]);
    setNewShot({ src: '', alt: '', viewport: 'mobile' });
  };
  const removeShot = (idx) => set('screenshots', form.screenshots.filter((_, i) => i !== idx));

  const inputStyle = {
    background: colors.input,
    border: `1px solid ${colors.cardBorder}`,
    color: colors.text,
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-xl p-5 space-y-4"
      style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}
      data-testid="admin-faq-editor"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="block text-xs font-semibold mb-1.5" style={{ color: colors.textMuted }}>Question</label>
          <input
            value={form.question}
            onChange={(e) => set('question', e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={inputStyle}
            placeholder="e.g. How do I upload a PDF?"
            data-testid="admin-faq-q"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: colors.textMuted }}>Category</label>
          <select
            value={form.category}
            onChange={(e) => set('category', e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={inputStyle}
            data-testid="admin-faq-cat"
          >
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold mb-1.5" style={{ color: colors.textMuted }}>
          Answer (markdown) — **bold**, *italic*, `code`, [links](/plans), bullet lists
        </label>
        <textarea
          value={form.answer}
          onChange={(e) => set('answer', e.target.value)}
          rows={10}
          className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-y font-mono"
          style={inputStyle}
          placeholder="Write the answer in markdown..."
          data-testid="admin-faq-answer"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="block text-xs font-semibold mb-1.5" style={{ color: colors.textMuted }}>Tags (comma-separated)</label>
          <input
            value={form.tags}
            onChange={(e) => set('tags', e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={inputStyle}
            placeholder="upload, pdf, files"
            data-testid="admin-faq-tags"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: colors.textMuted }}>Sort order</label>
          <input
            type="number"
            value={form.order}
            onChange={(e) => set('order', e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={inputStyle}
            data-testid="admin-faq-order"
          />
        </div>
      </div>

      {/* Screenshots */}
      <div>
        <div className="text-xs font-semibold mb-1.5" style={{ color: colors.textMuted }}>Screenshots</div>
        {form.screenshots.length > 0 && (
          <ul className="space-y-1.5 mb-2">
            {form.screenshots.map((s, i) => (
              <li key={i} className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg" style={{ background: colors.input, border: `1px solid ${colors.cardBorder}`, color: colors.text }}>
                <span className="uppercase tracking-wide" style={{ color: colors.textMuted }}>{s.viewport}</span>
                <code className="flex-1 truncate">{s.src}</code>
                <button type="button" onClick={() => removeShot(i)} className="p-1 rounded hover:bg-red-500/20" data-testid={`admin-faq-shot-remove-${i}`}>
                  <X className="h-3.5 w-3.5" style={{ color: '#ef4444' }} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap gap-2">
          <input
            value={newShot.src}
            onChange={(e) => setNewShot({ ...newShot, src: e.target.value })}
            className="flex-1 min-w-[220px] px-3 py-2 rounded-lg text-sm outline-none"
            style={inputStyle}
            placeholder="/faq/mobile-chat.png"
            data-testid="admin-faq-shot-src"
          />
          <input
            value={newShot.alt}
            onChange={(e) => setNewShot({ ...newShot, alt: e.target.value })}
            className="flex-1 min-w-[180px] px-3 py-2 rounded-lg text-sm outline-none"
            style={inputStyle}
            placeholder="Alt text"
          />
          <select
            value={newShot.viewport}
            onChange={(e) => setNewShot({ ...newShot, viewport: e.target.value })}
            className="px-3 py-2 rounded-lg text-sm outline-none"
            style={inputStyle}
          >
            <option value="mobile">Mobile</option>
            <option value="desktop">Desktop</option>
          </select>
          <Button type="button" onClick={addShot} variant="secondary" size="sm" data-testid="admin-faq-shot-add">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer" style={{ color: colors.text }}>
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => set('published', e.target.checked)}
            data-testid="admin-faq-published"
          />
          Published (visible on public FAQ)
        </label>
        {!isNew && (
          <span className="text-xs" style={{ color: colors.textMuted }}>id: <code>{form.id || slugify(form.question)}</code></span>
        )}
      </div>

      <div className="flex gap-2 justify-end pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving} data-testid="admin-faq-cancel">
          Cancel
        </Button>
        <Button type="submit" disabled={saving} data-testid="admin-faq-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
          Save
        </Button>
      </div>
    </form>
  );
}

export default function AdminFAQPage() {
  const { colors } = useAdminTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // 'new' or an item id
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/faq`, { withCredentials: true });
      setItems(res.data.items || []);
    } catch (_) {
      toast.error('Failed to load FAQ items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upsert = async (payload) => {
    setSaving(true);
    try {
      const existing = items.find((i) => i.id === payload.id);
      if (existing) {
        const res = await axios.patch(`${API}/admin/faq/${payload.id}`, payload, { withCredentials: true });
        setItems(items.map((i) => (i.id === payload.id ? res.data.item : i)));
        toast.success('Saved');
      } else {
        const res = await axios.post(`${API}/admin/faq`, payload, { withCredentials: true });
        setItems([...items, res.data.item]);
        toast.success('Created');
      }
      setEditingId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this FAQ item? This can\'t be undone.')) return;
    try {
      await axios.delete(`${API}/admin/faq/${id}`, { withCredentials: true });
      setItems(items.filter((i) => i.id !== id));
      toast.success('Deleted');
    } catch (_) {
      toast.error('Delete failed');
    }
  };

  const togglePublished = async (item) => {
    try {
      const res = await axios.patch(`${API}/admin/faq/${item.id}`, { published: !item.published }, { withCredentials: true });
      setItems(items.map((i) => (i.id === item.id ? res.data.item : i)));
    } catch (_) {
      toast.error('Failed to update visibility');
    }
  };

  const seedDefaults = async (replace = false) => {
    const label = replace ? 'Replace' : 'Import';
    if (!window.confirm(`${label} the collection with the ${FAQ_SEED.length} shipped default FAQ items? ${replace ? 'All existing custom items will be deleted.' : 'Existing items with the same id will be overwritten.'}`)) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/faq/seed`, { items: FAQ_SEED, replace }, { withCredentials: true });
      await load();
      toast.success(`Seeded ${FAQ_SEED.length} items`);
    } catch (_) {
      toast.error('Seed failed');
    } finally {
      setSaving(false);
    }
  };

  const filtered = items.filter((i) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (
      i.question.toLowerCase().includes(q) ||
      (i.tags || []).some((t) => t.toLowerCase().includes(q)) ||
      i.category.toLowerCase().includes(q)
    );
  });

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="admin-faq-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: colors.text }}>FAQ Content</h1>
            <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
              Manage the questions, answers and screenshots shown at{' '}
              <a href="/faq" target="_blank" rel="noreferrer" className="underline">/faq</a>.
              Changes go live the moment you save.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => seedDefaults(false)} disabled={saving} data-testid="admin-faq-seed-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Import defaults
            </Button>
            <Button onClick={() => setEditingId('new')} disabled={saving} data-testid="admin-faq-new-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> New question
            </Button>
          </div>
        </div>

        {/* Filter */}
        <div>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by question, tag, or category..."
            className="w-full px-4 py-2 rounded-lg text-sm outline-none"
            style={{ background: colors.input, border: `1px solid ${colors.cardBorder}`, color: colors.text }}
            data-testid="admin-faq-filter"
          />
        </div>

        {/* Editor for New / Edit */}
        {editingId === 'new' && (
          <EditorForm
            item={null}
            colors={colors}
            saving={saving}
            isNew
            onSave={upsert}
            onCancel={() => setEditingId(null)}
          />
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-12" style={{ color: colors.textMuted }}>
            <Loader2 className="h-6 w-6 mx-auto animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl px-6 py-14 text-center border border-dashed" style={{ borderColor: colors.cardBorder, color: colors.textMuted }}>
            <p className="text-sm">No FAQ items yet.</p>
            <p className="text-xs mt-1">Tap <em>Import defaults</em> to load the 32 shipped questions, or create your own.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {CATEGORIES.map((cat) => {
              const catItems = filtered.filter((i) => i.category === cat.id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
              if (catItems.length === 0) return null;
              return (
                <div key={cat.id} className="rounded-xl overflow-hidden" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}>
                  <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: colors.input, borderBottom: `1px solid ${colors.cardBorder}` }}>
                    <ArrowUpDown className="h-3.5 w-3.5" style={{ color: colors.textMuted }} />
                    <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: colors.textMuted }}>{cat.label}</span>
                    <span className="text-xs ml-auto" style={{ color: colors.textMuted }}>{catItems.length}</span>
                  </div>
                  <div>
                    {catItems.map((item) => (
                      <React.Fragment key={item.id}>
                        {editingId === item.id ? (
                          <div className="p-3">
                            <EditorForm
                              item={item}
                              colors={colors}
                              saving={saving}
                              onSave={upsert}
                              onCancel={() => setEditingId(null)}
                            />
                          </div>
                        ) : (
                          <div className="px-4 py-3 flex items-start gap-3 border-b last:border-b-0" style={{ borderColor: colors.cardBorder }} data-testid={`admin-faq-row-${item.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium" style={{ color: colors.text }}>{item.question}</span>
                                {!item.published && (
                                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
                                    Hidden
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] mt-1 flex flex-wrap gap-2" style={{ color: colors.textMuted }}>
                                <code>{item.id}</code>
                                {item.tags?.length > 0 && <span>· {item.tags.join(', ')}</span>}
                                {item.screenshots?.length > 0 && <span>· {item.screenshots.length} screenshot{item.screenshots.length === 1 ? '' : 's'}</span>}
                                <span>· order {item.order ?? 0}</span>
                              </div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <button type="button" onClick={() => togglePublished(item)} className="p-2 rounded hover:bg-white/5" title={item.published ? 'Hide' : 'Publish'} data-testid={`admin-faq-toggle-${item.id}`}>
                                {item.published ? <Eye className="h-4 w-4" style={{ color: colors.textMuted }} /> : <EyeOff className="h-4 w-4" style={{ color: '#f59e0b' }} />}
                              </button>
                              <button type="button" onClick={() => setEditingId(item.id)} className="p-2 rounded hover:bg-white/5" title="Edit" data-testid={`admin-faq-edit-${item.id}`}>
                                <Edit2 className="h-4 w-4" style={{ color: colors.textMuted }} />
                              </button>
                              <button type="button" onClick={() => remove(item.id)} className="p-2 rounded hover:bg-red-500/10" title="Delete" data-testid={`admin-faq-delete-${item.id}`}>
                                <Trash2 className="h-4 w-4" style={{ color: '#ef4444' }} />
                              </button>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
